import React from 'react';
import { Box, TextField } from '@mui/material';
import { Formik, Form } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';
import { formatRut } from 'react-rut-formatter';
import { setRut, useQuery } from '../../../../../../utils/helpers';
import { rutValidationSchema } from '../../../validationSchemas';
import ZubaleUserInputForm from '../../../../ZubaleUserInputForm';
import type { PLRFormProps } from '../../../../../../types/preloan';
import type { FormSchemaV3 } from '../../../../../../types/preloan/v3';
import TalanaTermsAndConditions from '../../../../TalanaTermsAndConditions';
import analytics, { resetAnalytics } from '../../../../../../utils/analytics';
import { inviteePlrCategory } from '../../../ComplementInvite/analyticsPages';
import {
  COMPLEMENT_DISCLOSURE, CREDIT_RESPONSIBILITY_NOTICE, invitedByLine,
} from '../../../ComplementInvite/copy';
import shouldShowPlrHero, { hasProjectName, projectEntityLabel } from '../../../heroVariant';
import type { Partner } from '../../../../../../components/LeadFormIndex/types';
import PlrShell, { isBancamePartner, partnerDisplayName } from '../../../landing/PlrShell';
import {
  BUREAU_NOTICE, RUT_SOURCES_NOTICE, controllerNotice, privacyPolicyUrl, termsUrl,
} from '../../../legal';
import {
  MobileStepHint, PlrButton, PlrFieldLabel, PlrLead, PlrTitle, ProjectLabel, TimeChip,
  plrInputSx,
} from '../../../landing/ui';
import { plr } from '../../../landing/tokens';
import { arrowRightIcon, shieldCheckIcon } from '../../../landing/icons';
import RecaptchaDialog from './RecaptchaDialog';

const DEFAULT_RUT_HEADER = '¿Calificas para este proyecto?';
// Sin proyecto (entidad "<Partner>-default" o sin entidad) no hay "este proyecto" al que apuntar.
const NO_PROJECT_RUT_HEADER = 'Evalúate con nosotros';
const TIME_CHIP_TEXT = 'Cerca de 3 minutos · Sin costo';

export default function RutForm<T extends FormSchemaV3>(props: PLRFormProps<T>) {
  const {
    formRef,
    formSchema,
    handleNext,
    handleEnableLoading,
    handleCurrentData,
  } = props;
  const query = useQuery();
  const showHero = shouldShowPlrHero({
    backgroundImage: formSchema.config.styles?.backgroundImage,
    hideHero: formSchema.config.preLoanRequestConfig?.hideHero,
    heroQuery: query.get('hero'),
    partnerName: formSchema.partner,
  });
  // Invitado al complemento: este paso reemplaza la bienvenida anterior, con sus textos.
  const invitee = Boolean(formSchema.complementToken);
  // Sin proyecto con nombre: "Evalúate con nosotros" + "Comenzar" (no "¿Calificas para este
  // proyecto?" + "Ver si califico").
  const hasProject = hasProjectName(formSchema.partnerEntityName);
  // proyecto: qué título vio ("¿Calificas para este proyecto?" o "Evalúate con nosotros").
  const onboardingCategory = inviteePlrCategory(formSchema.complementToken, 'PLR - ONBOARDING');
  const pageProps = { hero: showHero, proyecto: hasProject };
  const partner = {
    partnerName: formSchema.partner,
    partnerEntityName: formSchema.partnerEntityName,
    config: formSchema.config,
    products: formSchema.products,
  } as Partner;
  React.useEffect(() => handleEnableLoading(false), []);
  // El reCAPTCHA se pide en un diálogo al tocar "Ver si califico" (con el RUT ya válido);
  // al pasarlo se envía el RUT con ese token.
  const [captchaOpen, setCaptchaOpen] = React.useState(false);
  const pendingValues = React.useRef<T | null>(null);

  const [zubaleUserInput, setZubaleUserInput] = React.useState(formSchema.userInput);

  const shouldCallBNPL = false;
  const invisibleRecaptchaRef = React.useRef<ReCAPTCHA>(null);

  const handleZubaleUserInput = (userInput: FormSchemaV3['userInput']) => {
    setZubaleUserInput(userInput);
  };

  React.useEffect(() => {
    if (!formSchema.complementToken) {
      resetAnalytics();
    }
    analytics.page(onboardingCategory, 'INGRESO RUT', pageProps);
  }, []);

  const recaptchaOnChange = () => {
    analytics.track('Click Checkbox', {
      texto: 'No soy un robot',
      initiator: 'usuario',
      hero: showHero,
    });
  };
  const submitRut = async (values: T, recaptchaValue: string) => {
    handleEnableLoading(true);
    let invisibleRecaptchaValue;
    if (invisibleRecaptchaRef.current && shouldCallBNPL) {
      await invisibleRecaptchaRef.current?.executeAsync();
      invisibleRecaptchaValue = invisibleRecaptchaRef.current?.getValue();
      if (!invisibleRecaptchaValue) return handleEnableLoading(false);
    }
    handleCurrentData({
      ...values,
      captchaToken: recaptchaValue,
      invisibleCaptchaToken: invisibleRecaptchaValue,
      origin: values.origin,
      userInput: zubaleUserInput,
      rut: formatRut(values.rut).toLocaleLowerCase(),
      shouldCallBNPL,
    });
    return handleNext();
  };

  const onCaptchaVerified = (token: string) => {
    setCaptchaOpen(false);
    if (pendingValues.current) submitRut(pendingValues.current, token);
  };

  // Funnel INGRESO RUT → DIALOGO CAPTCHA → RESULTADO PRELIMINAR: separa a quien no toca el botón
  // de quien abandona en el reCAPTCHA (X, fondo o Esc = Cerrar Dialog).
  const openCaptcha = () => {
    analytics.page(onboardingCategory, 'DIALOGO CAPTCHA', pageProps);
    setCaptchaOpen(true);
  };
  const closeCaptcha = () => {
    analytics.track('Cerrar Dialog', { initiator: 'usuario', dialogo: 'DIALOGO CAPTCHA', hero: showHero });
    setCaptchaOpen(false);
  };

  return (
    <Formik
      innerRef={formRef}
      initialValues={formSchema}
      validationSchema={rutValidationSchema}
      onSubmit={(values) => {
        pendingValues.current = values;
        openCaptcha();
      }}
    >
      {({
        values, handleBlur, touched, errors, setFieldValue, submitCount,
      }) => {
        // Un campo vacío no se marca en rojo solo por salir de él: el error aparece cuando
        // hay algo escrito o al intentar continuar.
        const showRutError = Boolean(touched.rut && errors.rut)
          && (Boolean(values.rut) || submitCount > 0);
        return (
          <Form style={{ width: '100%' }}>
            <PlrShell
              partner={partner}
              showHero={showHero}
              photoBand
              progress={{ step: 1, label: 'Tu RUT' }}
              progressOnMobile={false}
              testId="preloan-rut-step"
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {showHero && (
                  <ProjectLabel hideOnPanel>
                    {projectEntityLabel(formSchema.partnerEntityName, formSchema.partner)}
                  </ProjectLabel>
                )}
                <TimeChip>{TIME_CHIP_TEXT}</TimeChip>
              </Box>
              {/* En una sola línea: el texto mide ~14,1 veces el tamaño de la fuente (Inter 800,
                -0.03em), así que la fuente se ajusta al ancho de la columna. El del invitado
                es más largo y usa dos líneas. */}
              <PlrTitle
                size="hero"
                testId="preloan-rut-header"
                sx={{
                  // Pegado al chip: chip y pregunta se leen como un solo título.
                  mt: '-6px',
                  fontSize: invitee
                    ? { xs: 26, sm: 34 }
                    : {
                      xs: 'min(26px, calc((100vw - 48px) / 14.6))',
                      sm: 'min(34px, calc((100vw - 144px) / 14.6))',
                    },
                  lineHeight: 1.15,
                  letterSpacing: '-0.03em',
                }}
              >
                {invitee && invitedByLine(formSchema.complementLeadFirstName || '')}
                {!invitee && (hasProject
                  ? DEFAULT_RUT_HEADER
                  : NO_PROJECT_RUT_HEADER)}
              </PlrTitle>
              <PlrLead large testId="preloan-rut-copy">
                {invitee && <span>{`${CREDIT_RESPONSIBILITY_NOTICE} `}</span>}
                <span>Ten a mano tu </span>
                <Box component="span" sx={{ color: plr.ink, fontWeight: 600 }}>ClaveÚnica</Box>
                <span>, te la pediremos.</span>
              </PlrLead>
              <Box sx={{
                display: 'flex', flexDirection: 'column', gap: '6px', mt: { xs: '4px', sm: '8px' },
              }}
              >
                <PlrFieldLabel
                  htmlFor="rut"
                  // En desktop (y en mobile con imagen=mini) el paso se ve en la barra de pasos.
                  aside={<MobileStepHint>Paso 1 de 3</MobileStepHint>}
                >
                  Tu RUT
                </PlrFieldLabel>
                <Box sx={{
                  display: 'flex', flexWrap: 'wrap', gap: { xs: '14px', sm: '12px' }, alignItems: 'flex-start',
                }}
                >
                  <TextField
                    variant="outlined"
                    autoComplete="off"
                    placeholder="12.345.678-9"
                    id="rut"
                    name="rut"
                    data-pw="preloan-rut-input"
                    autoFocus
                    inputProps={{
                      inputMode: 'text',
                      'aria-describedby': 'rut-helper-text preloan-rut-consent',
                    }}
                    error={showRutError}
                    // Aviso de que con el RUT se consultan deudas, siempre visible antes del CTA;
                    // si hay error, va arriba del aviso.
                    helperText={(
                      <>
                        {showRutError && (
                          <Box component="span" sx={{ display: 'block', mb: '2px' }}>{errors.rut}</Box>
                        )}
                        <Box
                          component="span"
                          data-pw="preloan-rut-bureau-notice"
                          sx={{ display: 'block', color: plr.body }}
                        >
                          {BUREAU_NOTICE}
                        </Box>
                      </>
                    )}
                    FormHelperTextProps={{
                      sx: { m: '6px 4px 0', fontSize: { xs: 12.5, sm: 13 }, lineHeight: 1.45 },
                    }}
                    value={values.rut}
                    onChange={(e) => setRut(setFieldValue, e.target.value)}
                    onBlur={(e) => {
                      setRut(setFieldValue, values.rut);
                      handleBlur(e);
                    }}
                    sx={{
                      flex: { xs: '1 1 100%', sm: '1 1 240px' },
                      minWidth: 0,
                      ...plrInputSx,
                      // Mobile: RUT y aviso centrados, alineados con el botón de ancho completo.
                      '& .MuiInputBase-input': { textAlign: { xs: 'center', sm: 'left' } },
                      '& .MuiFormHelperText-root': { textAlign: { xs: 'center', sm: 'left' } },
                    }}
                  />
                  <PlrButton
                    type="submit"
                    testId="preloan-submit-button"
                    // RUT vacío o inválido: el diálogo no se abre, así que este es el único
                    // registro de ese intento (también con Enter, que hace clic en este botón).
                    onClick={() => {
                      const rutError = values.rut ? errors.rut : 'Ingresa tu RUT';
                      if (rutError) {
                        analytics.track('ERROR', {
                          initiator: 'usuario', campo: 'rut', text: rutError, ...pageProps,
                        });
                      }
                    }}
                    sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
                  >
                    <span>{!invitee && hasProject ? 'Ver si califico' : 'Comenzar'}</span>
                    {arrowRightIcon()}
                  </PlrButton>
                </Box>
              </Box>
              {shouldCallBNPL && (
                <ReCAPTCHA
                  size="invisible"
                  ref={invisibleRecaptchaRef}
                  sitekey={process.env.REACT_APP_INVISIBLE_RECAPTCHA_SITE_KEY as string}
                />
              )}
              <RecaptchaDialog
                open={captchaOpen}
                siteKey={process.env.REACT_APP_CAPTCHA_PLRV2 as string}
                onClose={closeCaptcha}
                onVerified={onCaptchaVerified}
                onCheck={recaptchaOnChange}
              />
              {/* Separa el formulario de la card legal: toma el alto que sobra para dejarla
                al fondo de la card (o de la pantalla en mobile), con un mínimo de aire. */}
              <Box aria-hidden="true" sx={{ flex: '1 0 auto', minHeight: { xs: 4, sm: 12 } }} />
              {/* Textos legales agrupados en una card discreta bajo el CTA. */}
              <Box
                data-pw="preloan-rut-legal-card"
                sx={{
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'flex-start',
                  background: plr.surface,
                  borderRadius: '14px',
                  p: { xs: '12px 14px', sm: '14px 16px' },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'flex', color: plr.muted, mt: '1px', flexShrink: 0,
                  }}
                >
                  {shieldCheckIcon({ size: 16 })}
                </Box>
                <Box
                  component="p"
                  id="preloan-rut-consent"
                  data-pw="preloan-rut-consent"
                  sx={{
                    m: 0,
                    fontSize: { xs: 12.5, sm: 13.5 },
                    lineHeight: { xs: 1.45, sm: 1.5 },
                    color: plr.body,
                  }}
                >
                  <span>
                    Al continuar aceptas los
                    {' '}
                    <a
                      href={termsUrl(values.origin)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => analytics.track('Click Link', {
                        text: 'términos y condiciones',
                        initiator: 'usuario',
                        hero: showHero,
                      })}
                    >
                      términos y condiciones
                    </a>
                    {' '}
                    y la
                    {' '}
                    <a
                      href={privacyPolicyUrl(formSchema.partner)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => analytics.track('Click Link', {
                        text: 'política de privacidad',
                        initiator: 'usuario',
                        hero: showHero,
                      })}
                    >
                      política de privacidad
                    </a>
                    .
                  </span>
                  {!isBancamePartner(formSchema.partner) && (
                  <Box component="span" data-pw="preloan-rut-controller" sx={{ display: 'block', mt: '4px' }}>
                    {controllerNotice(partnerDisplayName(partner))}
                  </Box>
                  )}
                  {/* Fuentes al final, bajo el responsable de los datos (Max, 2 Nov). */}
                  <Box component="span" data-pw="preloan-rut-sources" sx={{ display: 'block', mt: '4px' }}>
                    {RUT_SOURCES_NOTICE}
                  </Box>
                  {invitee && (
                  <Box component="span" data-pw="preloan-rut-complement-disclosure" sx={{ display: 'block', mt: '4px' }}>
                    {COMPLEMENT_DISCLOSURE}
                  </Box>
                  )}
                </Box>
              </Box>
              {values.origin === 'zubale' && (
              <ZubaleUserInputForm
                formSchema={formSchema}
                onSubmit={handleZubaleUserInput}
              />
              )}
              {values.origin === 'talana' && !values.optIn && <TalanaTermsAndConditions />}
            </PlrShell>
          </Form>
        );
      }}

    </Formik>
  );
}
